// MO4.1.3
// @dynamic-i18n-keys
// Dynamic t() at line 112: template deviationview.tierCard.state.${state} (state: draft | pending_deletion | deleted).
//   deviationview.tierCard.state.draft
//   deviationview.tierCard.state.pending_deletion
//   deviationview.tierCard.state.deleted
import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import type { PapiDeviation, PapiDeviationTier, PapiUser } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import UserIdentity from '../../User/Identity';
import type { LayoutStyleProperties } from '../../types';
import TierPriceWithBadge from '../Helper/TierPriceWithBadge';
import TierStatisticsBar from '../Helper/StatisticsBar/TierStatisticsBar';
import DeviationDumbView from '../Dumbo';
import UnknownDumbo from '../Dumbo/Unknown';

import s from './VerticalTierPromotionCardDumb.scss';

const COVER_WIDTH_TO_HEIGHT_RATIO = 0.3987;

export interface Props {
  title?: string;
  description?: string;
  author?: PapiUser;
  showAuthor?: boolean;
  coverImage?: PapiDeviation;
  showMatureCover?: boolean;
  stats?: PapiDeviationTier['stats'];
  isBoosted?: boolean;
  price?: string;
  state?: 'draft' | 'active' | 'pending_deletion' | 'deleted';
  width?: number;
  height?: number;
  /** used in grids for dynamic positioning to not wrap every item in a container
   * do not abuse for anything else or you will get fire ants in your socks
   */
  style?: LayoutStyleProperties;
  showEmptyPlaceholders?: boolean;
  className?: string;
  hotspot?: React.ReactNode;
  ctaButton?: React.ReactNode;
  ctaWrapperClassName?: string;
  layout?: 'small' | 'medium' | 'big';
  onClick?: () => void;
  innerRef?: (node: HTMLElement | null) => void;
}

const VerticalTierPromotionCardDumb: React.FC<Props> = ({
  title,
  description,
  author,
  showAuthor = true,
  stats,
  isBoosted,
  price,
  coverImage,
  showMatureCover,
  ctaButton,
  width,
  height,
  style,
  showEmptyPlaceholders,
  state = 'active',
  className,
  ctaWrapperClassName,
  onClick,
  innerRef,
}) => {
  const { t } = useTranslation();

  if (!width) {
    return null;
  }

  const dumboWidth = width - (32 + 2);
  const dumboHeight = dumboWidth * COVER_WIDTH_TO_HEIGHT_RATIO;
  return (
    <div
      className={classnames(s['root'], className)}
      style={{ width: width, maxHeight: height, ...style }}
      onClick={onClick}
      ref={innerRef}
    >
      {showAuthor && author && (
        <UserIdentity user={author} size="small" className={s['identity']} />
      )}

      <div className={s['dumbo-wrapper']}>
        {coverImage ? (
          <DeviationDumbView
            className={s['dumbo']}
            deviation={coverImage}
            width={dumboWidth}
            height={dumboHeight}
            showMatureContent={showMatureCover}
          />
        ) : showEmptyPlaceholders ? (
          <div
            className={s['cover-placeholder']}
            style={{ width: dumboWidth, height: dumboHeight }}
          />
        ) : (
          <UnknownDumbo className={s['dumbo']} width={dumboWidth} height={dumboHeight} />
        )}
        {state !== 'active' && (
          <div className={classnames(s['state-tag'], s[state])}>
            {t(`deviationview.tierCard.state.${state}`)}
          </div>
        )}
      </div>

      {title ? (
        <div className={s['title']}>{title}</div>
      ) : (
        showEmptyPlaceholders && <div className={s['title-placeholder']} />
      )}

      <TierStatisticsBar className={s['statistics']} stats={stats} isBoosted={isBoosted} />

      <div className={s['description']}>{description}</div>

      <hr className={s['divider']} />
      <Flex alignItems="center" className={ctaWrapperClassName}>
        <TierPriceWithBadge
          className={s['price']}
          price={price}
          showPlaceholder={showEmptyPlaceholders}
        />
        {ctaButton}
      </Flex>
    </div>
  );
};
VerticalTierPromotionCardDumb.displayName = 'VerticalTierPromotionCardDumb';

export default VerticalTierPromotionCardDumb;
